import AlertIcon from '@mui/icons-material/Warning';
import { Collapse, useMediaQuery } from '@mui/material';
import IconButton, { IconButtonProps } from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import { styled, useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import dayjs from 'dayjs';
import React, { useMemo } from 'react';
import { gql, useMutation } from '@apollo/client';
import { Maybe } from '../../../../types/generated-types';
import {
  useDeviceAlerts,
  usePanelAlerts,
  useZoneAlerts,
} from '../../../system/AlertsManager';
import { useAuthorizer } from '../../../auth/AuthorizationContext';
import { Notifier } from '../../../system/services/notificationManager';
import BorderedSection from '../../shared/borderedSection';
import { humanize } from '../../../ui/shell/utilities';

const FORCE_RESOLVE_ALERT_MUTATION = gql`
  mutation ForceResolveAlert($_id: String!) {
    forceResolveAlert(_id: $_id) {
      success
      message
    }
  }
`;

interface ExpandSectionButtonProps extends IconButtonProps {
  expand: boolean;
  showDivider?: boolean;
  showLeftDivider?: boolean;
  showRightDivider?: boolean;
  buttonLabel: string;
  onClick: (event: any) => void;
}

export const ExpandSectionButton = styled((props: ExpandSectionButtonProps) => {
  const {
    expand,
    buttonLabel,
    onClick,
    showDivider,
    showRightDivider,
    showLeftDivider,
    ...other
  } = props;
  return (
    <div
      style={
        showDivider
          ? {
              padding: '5px',
              height: '100%',
              borderTopLeftRadius: 0,
              borderBottomLeftRadius: 0,
              borderLeft: '1px solid rgba(0, 0, 0, 0.23)',
            }
          : showRightDivider && showLeftDivider
            ? {
                height: '100%',
                borderTopRightRadius: 0,
                borderBottomRightRadius: 0,
                borderTopLeftRadius: 0,
                borderBottomLeftRadius: 0,
                borderRight: '1px solid rgba(0, 0, 0, 0.23)',
                borderLeft: '1px solid rgba(0, 0, 0, 0.23)',
              }
            : showLeftDivider
              ? {
                  height: '100%',
                  borderTopLeftRadius: 0,
                  borderBottomLeftRadius: 0,
                  borderLeft: '1px solid rgba(0, 0, 0, 0.23)',
                }
              : showRightDivider
                ? {
                    height: '100%',
                    borderTopRightRadius: 0,
                    borderBottomRightRadius: 0,
                    borderRight: '1px solid rgba(0, 0, 0, 0.23)',
                  }
                : {}
      }
    >
      <IconButton
        style={showDivider ? { padding: '4px' } : {}}
        onClick={onClick}
        aria-expanded={expand}
        aria-label={buttonLabel}
        {...other}
      />
    </div>
  );
})(({ theme, expand }) => ({
  transform: !expand ? 'rotate(0deg)' : 'rotate(180deg)',
  marginLeft: 'auto',
  transition: theme.transitions.create('transform', {
    duration: theme.transitions.duration.shortest,
  }),
}));

export function ToggleAlertDetailsButton<
  D extends {
    _id?: string;
    deviceId?: string | Maybe<string> | undefined;
    panelId?: string | Maybe<string> | undefined;
    zoneId?: string | Maybe<string> | undefined;
    sourceId?: string | Maybe<string> | undefined;
    __typename?: string;
  },
>(
  props: {
    showLeftDivider?: boolean;
    showRightDivider?: boolean;
    showDivider?: boolean;
    device: D;
  } & ExpandSectionButtonProps,
) {
  const deviceAlerts = useDeviceAlerts(props.device);
  const zoneAlerts = useZoneAlerts(
    (props.device.zoneId ?? props.device.__typename === 'Zone')
      ? props.device._id
      : undefined,
    props.device.sourceId,
  );

  const panelAlerts = usePanelAlerts(
    (props.device.panelId ?? props.device.__typename === 'Panel')
      ? props.device._id
      : undefined,
    props.device.sourceId,
  );

  const alerts = useMemo(() => {
    if (!props.device._id) return [];

    switch (props.device.__typename) {
      case 'Panel':
        return panelAlerts;
      case 'Zone':
        return zoneAlerts;
      default:
        return deviceAlerts;
    }
  }, [
    props.device._id,
    props.device.__typename,
    deviceAlerts,
    zoneAlerts,
    panelAlerts,
  ]);

  const showDivider = props.showDivider ?? false;
  const showLeftDivider = props.showLeftDivider ?? false;
  const showRightDivider = props.showRightDivider ?? false;

  return alerts.length > 0 ? (
    <ExpandSectionButton
      showDivider={showDivider}
      showLeftDivider={showLeftDivider}
      showRightDivider={showRightDivider}
      expand={props.expand}
      buttonLabel={props.buttonLabel}
      onClick={props.onClick}
    >
      <AlertIcon sx={{ color: 'rgba(255, 0, 0, 1)' }} fontSize="inherit" />
    </ExpandSectionButton>
  ) : null;
}

export function DeviceAlerts<
  D extends {
    _id?: Maybe<string> | string | undefined;
    deviceId?: Maybe<string> | string | undefined;
    sourceId?: Maybe<string> | string | undefined;
    __typename?: string;
  },
>({ device, alertsExpanded }: { device: D; alertsExpanded: boolean }) {
  const deviceAlerts = useDeviceAlerts(device);
  const zoneAlerts = useZoneAlerts(device._id, device.sourceId);
  const panelAlerts = usePanelAlerts(device._id, device.sourceId);
  const { can } = useAuthorizer();
  const [forceResolveAlert] = useMutation(FORCE_RESOLVE_ALERT_MUTATION);

  const handleResolve = async (alertId: string) => {
    try {
      const result = await forceResolveAlert({
        variables: { _id: alertId },
      });
      const data = result.data?.forceResolveAlert;
      if (data?.success) {
        Notifier.success(data.message);
      } else {
        Notifier.error(data?.message ?? 'Failed to resolve alert');
      }
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to resolve alert';
      Notifier.error(message);
    }
  };

  const alerts = React.useMemo(() => {
    if (!device?._id) return [];

    switch (device.__typename) {
      case 'Panel':
        return panelAlerts;
      case 'Zone':
        return zoneAlerts;
      default:
        return deviceAlerts;
    }
  }, [device?._id, device?.__typename, deviceAlerts, zoneAlerts, panelAlerts]);

  const hasAlerts = alerts.length > 0;
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'));
  return hasAlerts ? (
    <Collapse in={alertsExpanded} timeout="auto">
      <BorderedSection
        title={'Alerts'}
        error={true}
        style={{
          margin: '0',
          padding: '0',
          boxShadow: 'none',
        }}
        ccStyle={{
          display: 'flex',
          flexDirection: 'row',
          padding: '0px',
        }}
      >
        <List
          dense={false}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            padding: '6px 10px',
            backgroundColor: 'white',
          }}
        >
          {hasAlerts ? (
            alerts.map(
              (alert, index) =>
                alert &&
                (() => {
                  const alertSubject = {
                    __typename: 'Alert' as const,
                    ...alert,
                  };
                  const canResolve = (
                    can as (
                      action: string,
                      subject: typeof alertSubject,
                    ) => boolean
                  )('resolveAlert', alertSubject);
                  const alertId =
                    '_id' in alert && typeof alert._id === 'string'
                      ? alert._id
                      : undefined;
                  return (
                    <ListItem
                      key={alertId ?? index}
                      sx={{
                        width: '100%',
                        display: 'flex',
                        flexDirection: 'row',
                        padding: 0,
                        borderTop: '1px solid lightgray',

                        '&:first-of-type': {
                          borderTop: 'none !important',
                          marginTop: 'inherit',
                        },
                      }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          width: '100%',
                        }}
                      >
                        <div
                          style={{
                            width: '100%',
                            display: 'flex',
                            flexDirection: 'row',
                            justifyContent: 'space-between',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          <span
                            style={{
                              textTransform: 'capitalize',
                            }}
                          >
                            {alert.device?.name || 'Unnamed Device'}
                          </span>
                          <span
                            style={{
                              textTransform: 'capitalize',
                            }}
                          >
                            {humanize(alert.device?.type || 'Unknown Device')}
                          </span>
                        </div>
                        {alert.meta?.message ? (
                          <div
                            style={{
                              width: '100%',
                              display: 'flex',
                              flexDirection: 'row',
                              justifyContent: 'space-between',
                            }}
                          >
                            <Typography
                              style={{
                                display: 'flex',
                                flexDirection: 'column',
                              }}
                            >
                              {alert.meta?.message}
                            </Typography>
                          </div>
                        ) : null}
                        <div
                          style={{
                            width: '100%',
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                            gap: 8,
                          }}
                        >
                          <Typography
                            component="span"
                            style={{
                              flex: 1,
                              minWidth: 0,
                              color: 'red',
                              letterSpacing: '0',
                              fontSize: isSmall ? '.96em' : '1em',
                            }}
                          >
                            {alert.type?.name || 'Unknown Alert Type'} since{' '}
                            {dayjs(alert.initiated).format(
                              canResolve && alertId ? 'M/D/YY h:mm A' : 'LLL',
                            )}
                          </Typography>
                          {canResolve && alertId ? (
                            <Button
                              size="small"
                              color="primary"
                              onClick={() => handleResolve(alertId)}
                              sx={{ flexShrink: 0 }}
                            >
                              Resolve
                            </Button>
                          ) : null}
                        </div>
                      </div>
                    </ListItem>
                  );
                })(),
            )
          ) : (
            <ListItem>
              <div>No Alerts!</div>
            </ListItem>
          )}
        </List>
      </BorderedSection>
    </Collapse>
  ) : null;
}
